// SPDX-License-Identifier: AGPL-3.0-or-later

// Channel glyph: an optional decoration shown between a channel's type icon (#, speaker…) and its
// name. Stored as one compact string so it travels through the API and gateway untouched:
//   ph:<icon-key>:<weight>:<rrggbb>   a Phosphor icon tinted with a colour
//   em:<emoji>                        a Unicode emoji
//   ce:<emoji-id>[:a]                 a guild custom emoji (":a" when animated)

export const CHANNEL_GLYPH_MAX_LENGTH = 80;

export const CHANNEL_GLYPH_WEIGHTS = ['regular', 'bold', 'fill'] as const;
export type ChannelGlyphWeight = (typeof CHANNEL_GLYPH_WEIGHTS)[number];

export type ChannelGlyph =
	| {kind: 'icon'; key: string; weight: ChannelGlyphWeight; color: string}
	| {kind: 'emoji'; emoji: string}
	| {kind: 'custom'; id: string; animated: boolean};

const ICON_PATTERN = /^ph:([a-z0-9-]{1,48}):(regular|bold|fill):([0-9a-f]{6})$/;
const EMOJI_PATTERN = /^em:([^\s:]{1,32})$/u;
const CUSTOM_PATTERN = /^ce:(\d{1,20})(:a)?$/;

export function parseChannelGlyph(value: string | null | undefined): ChannelGlyph | null {
	if (!value || value.length > CHANNEL_GLYPH_MAX_LENGTH) return null;
	const icon = ICON_PATTERN.exec(value);
	if (icon) return {kind: 'icon', key: icon[1], weight: icon[2] as ChannelGlyphWeight, color: icon[3]};
	const emoji = EMOJI_PATTERN.exec(value);
	if (emoji) return {kind: 'emoji', emoji: emoji[1]};
	const custom = CUSTOM_PATTERN.exec(value);
	if (custom) return {kind: 'custom', id: custom[1], animated: custom[2] != null};
	return null;
}

export function formatChannelGlyph(glyph: ChannelGlyph): string {
	switch (glyph.kind) {
		case 'icon':
			return `ph:${glyph.key}:${glyph.weight}:${glyph.color.replace(/^#/, '').toLowerCase()}`;
		case 'emoji':
			return `em:${glyph.emoji}`;
		case 'custom':
			return `ce:${glyph.id}${glyph.animated ? ':a' : ''}`;
	}
}

export function isValidChannelGlyph(value: string): boolean {
	return parseChannelGlyph(value) != null;
}
